import React, { useState, useEffect, useRef } from 'react'
import { Settings, Globe, Clock, Calendar, Sun, Moon, Palette, Volume2, UtensilsCrossed, MapPin, Loader2 } from 'lucide-react'
import { apiClient } from '@/utils/api'
import { getErrorMessage } from '@/utils/helpers'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'

interface GeneralSettings {
  timezone: string
  date_format: string
  time_format: string
  week_starts_on: string
  theme: string
  language: string
  sound_effects: boolean
  animations: boolean
  default_view: string
  items_per_page: number
  currency_symbol: string
  point_value: number
}

interface MealSettings {
  enabled_meal_types: string[]
  allow_dinner_opt_out: boolean
  meal_vote_deadline_hours: number
}

// system_settings keys read by GET /api/settings/public/meal-config and
// GET /api/kids/meal-config (KidMealPlanner). Writes go through the
// parent-only PUT /api/settings/:key.
const MEAL_SETTING_KEYS = {
  enabledTypes: 'meal_enabled_types',
  allowDinnerOptOut: 'meal_allow_dinner_opt_out',
  voteDeadlineHours: 'meal_vote_deadline_hours'
} as const

const DEFAULT_MEAL_SETTINGS: MealSettings = {
  enabled_meal_types: ['dinner'],
  allow_dinner_opt_out: true,
  meal_vote_deadline_hours: 4
}

interface LocationSettings {
  latitude: number | null
  longitude: number | null
  display_name: string | null
}

const DEFAULT_GENERAL_SETTINGS: GeneralSettings = {
  timezone: 'America/New_York',
  date_format: 'MM/DD/YYYY',
  time_format: '12h',
  week_starts_on: 'sunday',
  theme: 'light',
  language: 'en',
  sound_effects: true,
  animations: true,
  default_view: 'dashboard',
  items_per_page: 25,
  currency_symbol: '$',
  point_value: 100
}

export default function GeneralSettings() {
  const { user } = useAuth()
  const isParent = user?.is_parent ?? false
  const [settings, setSettings] = useState<GeneralSettings>(DEFAULT_GENERAL_SETTINGS)
  // Full preferences object, so saving the "general" block does not wipe
  // other keys (PUT /api/user/preferences replaces the whole column).
  const preferencesRef = useRef<Record<string, unknown>>({})

  const [mealSettings, setMealSettings] = useState<MealSettings>(DEFAULT_MEAL_SETTINGS)

  const [location, setLocation] = useState<LocationSettings>({
    latitude: null,
    longitude: null,
    display_name: null
  })
  const [zipCode, setZipCode] = useState('')
  const [savingLocation, setSavingLocation] = useState(false)

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu'
  ]

  const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
    { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 31, 2024)' }
  ]

  const themes = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'auto', label: 'Auto (System)', icon: Settings }
  ]

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      const response = await apiClient.get('/api/user/general-settings')
      const prefs = response?.preferences
      if (prefs && typeof prefs === 'object') {
        preferencesRef.current = prefs
        if (prefs.general && typeof prefs.general === 'object') {
          setSettings({ ...DEFAULT_GENERAL_SETTINGS, ...prefs.general })
        }
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load your settings'))
    }

    try {
      const response = await apiClient.get('/api/settings/public/meal-config')
      const mealConfig = response?.data ?? response
      if (mealConfig) {
        setMealSettings({
          enabled_meal_types: Array.isArray(mealConfig.enabled_meal_types)
            ? mealConfig.enabled_meal_types
            : DEFAULT_MEAL_SETTINGS.enabled_meal_types,
          allow_dinner_opt_out: mealConfig.allow_dinner_opt_out ?? DEFAULT_MEAL_SETTINGS.allow_dinner_opt_out,
          meal_vote_deadline_hours:
            Number(mealConfig.meal_vote_deadline_hours) || DEFAULT_MEAL_SETTINGS.meal_vote_deadline_hours
        })
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load meal settings'))
    }

    try {
      const locationResponse = await apiClient.get('/api/settings/location')
      if (locationResponse?.success && locationResponse.data) {
        setLocation({
          latitude: locationResponse.data.latitude,
          longitude: locationResponse.data.longitude,
          display_name: locationResponse.data.display_name
        })
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load location'))
    } finally {
      setLoading(false)
    }
  }

  const applyTheme = (theme: string) => {
    const dark = theme === 'dark' || (theme === 'auto' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    document.documentElement.classList.toggle('dark', dark)
  }

  const saveMealSettings = async () => {
    await apiClient.put(`/api/settings/${MEAL_SETTING_KEYS.enabledTypes}`, {
      value: JSON.stringify(mealSettings.enabled_meal_types)
    })
    await apiClient.put(`/api/settings/${MEAL_SETTING_KEYS.allowDinnerOptOut}`, {
      value: String(mealSettings.allow_dinner_opt_out)
    })
    await apiClient.put(`/api/settings/${MEAL_SETTING_KEYS.voteDeadlineHours}`, {
      value: String(mealSettings.meal_vote_deadline_hours)
    })
  }

  const saveSettings = async () => {
    setSaving(true)
    try {
      const preferences = { ...preferencesRef.current, general: settings }
      try {
        await apiClient.put('/api/user/preferences', { preferences })
        preferencesRef.current = preferences
        applyTheme(settings.theme)
      } catch (error) {
        toast.error(getErrorMessage(error, 'Failed to save settings'))
        return
      }

      if (isParent) {
        try {
          await saveMealSettings()
        } catch (error) {
          toast.error(getErrorMessage(error, 'Failed to save meal settings'))
          return
        }
      }

      toast.success('Settings saved!')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = (key: keyof GeneralSettings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleChange = (key: keyof GeneralSettings, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const saveLocation = async () => {
    if (!zipCode.trim()) {
      toast.error('Please enter a zip code')
      return
    }

    setSavingLocation(true)
    try {
      const response = await apiClient.put('/api/settings/location', {
        zip_code: zipCode.trim()
      })

      if (response?.success && response.data) {
        setLocation({
          latitude: response.data.latitude,
          longitude: response.data.longitude,
          display_name: response.data.display_name
        })
        setZipCode('')
        toast.success('Location updated successfully')
      } else {
        toast.error(response?.error || 'Failed to update location')
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update location'))
    } finally {
      setSavingLocation(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Regional Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Globe className="w-5 h-5 mr-2 text-blue-500" />
          Regional Settings
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Timezone
            </label>
            <select
              value={settings.timezone}
              onChange={(e) => handleChange('timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              {timezones.map(tz => (
                <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Language
            </label>
            <select
              value={settings.language}
              onChange={(e) => handleChange('language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="en">English</option>
              <option value="es">Español (Coming Soon)</option>
              <option value="fr">Français (Coming Soon)</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date Format
            </label>
            <select
              value={settings.date_format}
              onChange={(e) => handleChange('date_format', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              {dateFormats.map(format => (
                <option key={format.value} value={format.value}>{format.label}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Format
            </label>
            <select
              value={settings.time_format}
              onChange={(e) => handleChange('time_format', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="12h">12-hour (1:30 PM)</option>
              <option value="24h">24-hour (13:30)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Location Settings (family-wide, parents only) */}
      {isParent && (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <MapPin className="w-5 h-5 mr-2 text-red-500" />
          Location
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Set your family's location for accurate weather information on the kiosk display.
        </p>

        {location.display_name && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-500">Current Location</div>
            <div className="font-medium text-gray-900">{location.display_name}</div>
          </div>
        )}

        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Enter zip code (e.g., 30064)"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && saveLocation()}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            maxLength={10}
          />
          <button
            onClick={saveLocation}
            disabled={savingLocation || !zipCode.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {savingLocation ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              'Update Location'
            )}
          </button>
        </div>
      </div>
      )}

      {/* Appearance */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Palette className="w-5 h-5 mr-2 text-purple-500" />
          Appearance
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Theme
            </label>
            <div className="grid grid-cols-3 gap-3">
              {themes.map(theme => {
                const Icon = theme.icon
                return (
                  <button
                    key={theme.value}
                    onClick={() => handleChange('theme', theme.value)}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      settings.theme === theme.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-6 h-6 mx-auto mb-1" />
                    <div className="text-sm font-medium">{theme.label}</div>
                  </button>
                )
              })}
            </div>
          </div>
          
          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Palette className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Animations</div>
                <div className="text-sm text-gray-500">Enable smooth transitions and effects</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.animations}
              onChange={() => handleToggle('animations')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Volume2 className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Sound Effects</div>
                <div className="text-sm text-gray-500">Play sounds for actions and notifications</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={settings.sound_effects}
              onChange={() => handleToggle('sound_effects')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
        </div>
      </div>

      {/* Calendar Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-green-500" />
          Calendar Settings
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Week Starts On
            </label>
            <select
              value={settings.week_starts_on}
              onChange={(e) => handleChange('week_starts_on', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="sunday">Sunday</option>
              <option value="monday">Monday</option>
              <option value="saturday">Saturday</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default View
            </label>
            <select
              value={settings.default_view}
              onChange={(e) => handleChange('default_view', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="dashboard">Dashboard</option>
              <option value="calendar">Calendar</option>
              <option value="chores">Chores</option>
              <option value="rewards">Rewards</option>
            </select>
          </div>
        </div>
      </div>

      {/* Meal Settings (family-wide, parents only) */}
      {isParent && (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <UtensilsCrossed className="w-5 h-5 mr-2 text-orange-500" />
          Meal Planning
        </h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enabled Meal Types
            </label>
            <div className="space-y-2">
              {['breakfast', 'lunch', 'dinner'].map(mealType => (
                <label key={mealType} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={mealSettings.enabled_meal_types.includes(mealType)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setMealSettings(prev => ({
                          ...prev,
                          enabled_meal_types: [...prev.enabled_meal_types, mealType]
                        }))
                      } else {
                        setMealSettings(prev => ({
                          ...prev,
                          enabled_meal_types: prev.enabled_meal_types.filter(t => t !== mealType)
                        }))
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 mr-2"
                  />
                  <span className="capitalize">{mealType}</span>
                </label>
              ))}
            </div>
          </div>
          
          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <div>
                <div className="font-medium">Allow Dinner Opt-Out</div>
                <div className="text-sm text-gray-500">Kids can opt out of family dinner</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={mealSettings.allow_dinner_opt_out}
              onChange={() => setMealSettings(prev => ({ ...prev, allow_dinner_opt_out: !prev.allow_dinner_opt_out }))}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vote Deadline (hours before meal)
            </label>
            <input
              type="number"
              min="1"
              max="24"
              value={mealSettings.meal_vote_deadline_hours}
              onChange={(e) => setMealSettings(prev => ({ ...prev, meal_vote_deadline_hours: parseInt(e.target.value) || 4 }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>
      )}

      {/* Points & Rewards */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2 text-yellow-500" />
          Points & Rewards
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency Symbol
            </label>
            <select
              value={settings.currency_symbol}
              onChange={(e) => handleChange('currency_symbol', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="$">$ Dollar</option>
              <option value="€">€ Euro</option>
              <option value="£">£ Pound</option>
              <option value="¥">¥ Yen</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Points to Currency Rate
            </label>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 10, label: 'Recommended' },
                  { value: 25, label: '' },
                  { value: 50, label: '' },
                  { value: 100, label: '' }
                ].map(option => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleChange('point_value', option.value)}
                    className={`px-3 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                      settings.point_value === option.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    <div>{option.value} pts = {settings.currency_symbol}1</div>
                    {option.label && (
                      <div className="text-xs text-blue-500 font-normal">{option.label}</div>
                    )}
                  </button>
                ))}
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Or enter a custom rate
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    value={settings.point_value}
                    onChange={(e) => {
                      const val = parseInt(e.target.value)
                      if (val > 0) handleChange('point_value', val)
                    }}
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  <span className="text-sm text-gray-600">points = {settings.currency_symbol}1</span>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                At {settings.point_value} pts/{settings.currency_symbol}1, a chore worth 5 points earns {settings.currency_symbol}{(5 / settings.point_value).toFixed(2)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Display Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2 text-gray-500" />
          Display Settings
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Items Per Page
          </label>
          <select
            value={settings.items_per_page}
            onChange={(e) => handleChange('items_per_page', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={10}>10 items</option>
            <option value={25}>25 items</option>
            <option value={50}>50 items</option>
            <option value={100}>100 items</option>
          </select>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={saveSettings}
          disabled={saving}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Settings'
          )}
        </button>
      </div>
    </div>
  )
}